import { supabase } from '@/integrations/supabase/client';
import { TradingAnalysisOrchestrator } from '@/services/trading-analysis/TradingAnalysisOrchestrator';
import { AnalysisMode, AnalysisConfig, PredictionResult } from '@/types/trading-analysis-types';

interface HonestBacktestPredictionResult {
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  confidence: number;
  winProbability: number;
  lossProbability: number;
  usedPatterns: number;
  patternsUsed: Array<{
    pattern: string;
    frequency: number;
    successRate: number;
    confidenceScore: number;
  }>;
  skipReason?: string;
}

interface Pattern {
  pattern: string;
  frequency: number;
  nextWinCount: number;
  nextLossCount: number;
  successRate: number;
  confidenceScore: number;
  lastSeenIndex: number;
}

interface ComparisonResult {
  currentPrediction: PredictionResult;
  honestBacktestPrediction: HonestBacktestPredictionResult;
  differences: {
    winProbabilityDiff: number;
    confidenceDiff: number;
    patternsCountDiff: number;
    predictionMatch: boolean;
  };
}

export class ManualPredictionComparisonService {
  /**
   * Manual prediction using honest backtest patterns from simulation
   */
  static async predictFromHonestSimulation(
    simulationId: string,
    inputSequence: string,
    threshold: number = 0.52
  ): Promise<HonestBacktestPredictionResult> {
    // Get patterns from honest_backtest_patterns table for the simulation
    const { data: patterns, error } = await supabase
      .from('honest_backtest_patterns')
      .select('*')
      .eq('simulation_id', simulationId)
      .order('confidence_score', { ascending: false });

    if (error) throw error;

    if (!patterns || patterns.length === 0) {
      throw new Error('No patterns available for this simulation. Please run honest backtest first.');
    }

    // Convert patterns to internal format
    const internalPatterns = patterns.map(row => ({
      pattern: row.pattern,
      frequency: row.frequency,
      nextWinCount: row.next_win_count,
      nextLossCount: row.next_loss_count,
      successRate: row.success_rate,
      confidenceScore: row.confidence_score,
      lastSeenIndex: row.position_last_updated || 0
    }));

    // Parse input sequence (only last 8 elements like honest backtest)
    const sequence = inputSequence.split(',').map(Number).filter(n => !isNaN(n));
    const recentSequence = sequence.slice(Math.max(0, sequence.length - 8));

    // Apply makeRealisticPrediction logic
    return this.makeRealisticPrediction(internalPatterns, recentSequence, threshold);
  }

  /**
   * Compare Current (suffix-based) vs Honest Backtest prediction logic
   */
  static async comparepredictions(
    fileId: string,
    inputSequence: string,
    mode: AnalysisMode = 'basic',
    customConfig?: Partial<AnalysisConfig>
  ): Promise<ComparisonResult> {
    // 1. Get Current Prediction (existing suffix-based logic)
    const currentPrediction = await TradingAnalysisOrchestrator.predictNextTrade(
      fileId,
      inputSequence,
      mode,
      customConfig
    );

    // 2. Get Honest Backtest Prediction (makeRealisticPrediction logic)
    const honestBacktestPrediction = await this.predictWithHonestBacktestLogic(
      fileId,
      inputSequence,
      mode,
      customConfig
    );

    // 3. Calculate differences
    const differences = {
      winProbabilityDiff: currentPrediction.winProbability - honestBacktestPrediction.winProbability,
      confidenceDiff: this.normalizeConfidence(currentPrediction.confidenceLevel) - honestBacktestPrediction.confidence,
      patternsCountDiff: (currentPrediction.matchingPatterns || 0) - honestBacktestPrediction.usedPatterns,
      predictionMatch: this.normalizePrediction(currentPrediction.prediction) === honestBacktestPrediction.prediction
    };

    return {
      currentPrediction,
      honestBacktestPrediction,
      differences
    };
  }

  /**
   * Implement makeRealisticPrediction logic for frontend
   */
  private static async predictWithHonestBacktestLogic(
    fileId: string,
    inputSequence: string,
    mode: AnalysisMode = 'basic',
    customConfig?: Partial<AnalysisConfig>
  ): Promise<HonestBacktestPredictionResult> {
    // Get patterns from database (same as current logic)
    const { data: patterns, error } = await supabase
      .from('trading_patterns')
      .select('*')
      .eq('file_id', fileId)
      .eq('analysis_mode', mode)
      .order('confidence_score', { ascending: false });

    if (error) throw error;

    if (!patterns || patterns.length === 0) {
      throw new Error('No patterns available for prediction. Please analyze patterns first.');
    }

    // Convert patterns to internal format
    const internalPatterns = patterns.map(row => ({
      pattern: row.pattern,
      frequency: row.frequency,
      nextWinCount: row.next_win_count,
      nextLossCount: row.next_loss_count,
      successRate: row.success_rate,
      confidenceScore: row.confidence_score,
      lastSeenIndex: row.last_seen_index || 0
    }));

    // Parse input sequence (only last 8 elements like honest backtest)
    const sequence = inputSequence.split(',').map(Number).filter(n => !isNaN(n));
    const recentSequence = sequence.slice(Math.max(0, sequence.length - 8));
    
    // Use threshold from config or default to 0.52
    const threshold = 0.52; // Default threshold since AnalysisConfig doesn't include threshold

    // Apply makeRealisticPrediction logic
    return this.makeRealisticPrediction(internalPatterns, recentSequence, threshold);
  }

  /**
   * Port of makeRealisticPrediction from honest-progressive-backtest
   */
  private static makeRealisticPrediction(
    patterns: Pattern[],
    recentSequence: number[],
    threshold: number
  ): HonestBacktestPredictionResult {
    const sequenceStr = recentSequence.join('');
    const allMatchingPatterns: Pattern[] = [];

    // PHASE 1: Collect ALL matching patterns (no early termination)
    for (const pattern of patterns) {
      if (sequenceStr.endsWith(pattern.pattern)) {
        allMatchingPatterns.push(pattern);
      }
    }

    // Sort patterns by quality (success rate first, then frequency, then length)
    allMatchingPatterns.sort((a, b) => {
      // Primary: Higher success rate
      if (Math.abs(a.successRate - b.successRate) > 5) {
        return b.successRate - a.successRate;
      }
      // Secondary: Higher frequency (reliability)
      if (Math.abs(a.frequency - b.frequency) > 1) {
        return b.frequency - a.frequency;
      }
      // Tertiary: Longer patterns (more specific)
      return b.pattern.length - a.pattern.length;
    });

    // Select top 8 patterns
    const matchingPatterns = allMatchingPatterns.slice(0, 8);

    // Default values
    let winProbability = 50.0;
    let lossProbability = 50.0;
    let prediction: 'WIN' | 'LOSS' | 'SKIP' = 'SKIP';
    let confidence = 0;
    let skipReason = 'No matching patterns found';

    if (matchingPatterns.length > 0) {
      // Improved weighted calculation
      let totalWeight = 0;
      let weightedWinRate = 0;
      
      for (const pattern of matchingPatterns) {
        // Enhanced confidence calculation
        const enhancedConfidence = this.calculateEnhancedConfidence(
          pattern.frequency, 
          pattern.successRate, 
          pattern.pattern.length
        );
        
        let successRate = pattern.successRate || 50;
        
        // Convert percentage to proper range
        if (successRate > 1) {
          successRate = successRate / 100;
        }
        
        successRate = Math.min(Math.max(successRate, 0), 1);
        
        totalWeight += enhancedConfidence;
        weightedWinRate += successRate * enhancedConfidence;
      }
      
      if (totalWeight > 0) {
        winProbability = (weightedWinRate / totalWeight) * 100;
        lossProbability = 100 - winProbability;
        
        // Enhanced confidence score calculation
        const avgConfidence = totalWeight / matchingPatterns.length;
        const patternCountBonus = Math.min(20, matchingPatterns.length * 2.5);
        confidence = Math.min(95, avgConfidence + patternCountBonus);
        
        // Make prediction using threshold
        const thresholdPercent = threshold * 100;
        const winThresholdPercent = thresholdPercent;
        const lossThresholdPercent = 100 - thresholdPercent;
        
        if (winProbability > winThresholdPercent) {
          prediction = 'WIN';
          skipReason = undefined;
        } else if (winProbability < lossThresholdPercent) {
          prediction = 'LOSS';
          skipReason = undefined;
        } else {
          prediction = 'SKIP';
          skipReason = `Mixed signals (${winProbability.toFixed(1)}% vs ${thresholdPercent}% threshold)`;
        }
      }
    }

    const patternsUsed = matchingPatterns.map(pattern => ({
      pattern: pattern.pattern,
      frequency: pattern.frequency,
      successRate: pattern.successRate,
      confidenceScore: this.calculateEnhancedConfidence(pattern.frequency, pattern.successRate, pattern.pattern.length)
    }));

    return {
      prediction,
      confidence,
      winProbability,
      lossProbability,
      usedPatterns: matchingPatterns.length,
      patternsUsed,
      skipReason
    };
  }

  /**
   * Enhanced confidence calculation from honest backtest
   */
  private static calculateEnhancedConfidence(frequency: number, successRate: number, patternLength: number): number {
    // Frequency weight (logarithmic scale for diminishing returns)
    const frequencyWeight = Math.min(40, 10 * Math.log10(frequency + 1));
    
    // Success rate weight (exponential for strong bias toward high success)
    const normalizedSuccessRate = Math.max(0, Math.min(100, successRate)) / 100;
    const successRateWeight = Math.pow(normalizedSuccessRate, 1.5) * 40;
    
    // Pattern length weight (longer patterns are more specific)
    const lengthWeight = Math.min(20, (patternLength - 2) * 4);
    
    // Base confidence (minimum viable pattern)
    const baseConfidence = 5;
    
    // Calculate total confidence
    const totalConfidence = baseConfidence + frequencyWeight + successRateWeight + lengthWeight;
    
    // Cap at reasonable maximum
    return Math.min(95, Math.max(5, totalConfidence));
  }

  /**
   * Normalize confidence level to number for comparison
   */
  private static normalizeConfidence(confidenceLevel?: string): number {
    switch (confidenceLevel) {
      case 'very_high': return 85;
      case 'high': return 70;
      case 'medium': return 55;
      case 'low': return 40;
      case 'very_low': return 25;
      default: return 25;
    }
  }

  /**
   * Normalize prediction result for comparison
   */
  private static normalizePrediction(prediction?: string): 'WIN' | 'LOSS' | 'SKIP' {
    switch (prediction) {
      case 'WIN': return 'WIN';
      case 'LOSS': return 'LOSS';
      case 'UNCERTAIN':
      case 'ERROR':
      default: return 'SKIP';
    }
  }
}